"use client"

import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { StatusPublish, VisibilityPublish } from "@/(admin)/modules/post/pages/StatusPublish"
import AccessDenied from "@/components/custom-access-denied"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { useVideo } from "@/hook/useVideo"
import { t } from "@/languages/i18n"
import { createPath } from "@/utils/create-path"
import { CheckCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { Button, Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import React, { useCallback, useEffect, useState } from "react"
import { VIDEO } from "../../../../constants/admin-paths"
import VideoFormFields from "../components/VideoFormFields"

const FormUpsertVideo: React.FC = () => {
  const [form] = Form.useForm()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const router = useRouter()
  const [status, setStatus] = useState("Draft")

  const isEditMode = Boolean(id)
  const { loading, fetchVideoById, createVideo, updateVideo, hasPermission } = useVideo()
  const [isFormReady, setIsFormReady] = useState(false);
  const getNormalizedSnapshot = useCallback(() => {
    const values = form.getFieldsValue(true)
    return {
      title: values.title || "",
      youtubeUrl: values.youtubeUrl || "",
      order: values.order ?? 99,

      categories: [...(values.categories || [])].sort(),
      status,
    }
  }, [form, status])

  const { checkChange, commitSnapshot, message, handleCancel: guardedHandleCancel } = useGuardedForm({
    form,
    getSnapshot: getNormalizedSnapshot,
  })


  useEffect(() => {
    checkChange()
  }, [status, checkChange])

  const loadVideo = useCallback(async () => {
    const videoData = await fetchVideoById(Number(id))
    if (videoData) {
      const categoryIds = videoData.attributes.categories?.data?.map((cat: any) => cat.id) || []
      form.setFieldsValue({
        title: videoData.attributes.title,
        youtubeUrl: videoData.attributes.youtubeUrl,
        order: videoData.attributes.order || 99,
        categories: categoryIds,
      })
      setStatus(videoData.attributes.publishedAt ? "Publish" : "Draft")
      setIsFormReady(true);
    }
  }, [id, fetchVideoById, form])

  const handleSubmit = async () => {
    const values = form.getFieldsValue()
    const payload = {
      title: values.title,
      youtubeUrl: values.youtubeUrl || "",
      order: values.order || 99,
      categories: values.categories ? values.categories : [],
      publishedAt: status === "Draft" ? null : new Date().toISOString(),
    }

    const success = isEditMode && id
      ? await updateVideo(id, payload)
      : await createVideo(payload)

    if (success) {
      commitSnapshot()
      message.success(isEditMode ? t("Cập nhật thành công!") : t("Tạo mới thành công!"))
      const targetPath = createPath(VIDEO.LIST.PATH, searchParams)
      router.push(targetPath)
    }
  }

  const handleCancel = () => {
    const targetPath = createPath(VIDEO.LIST.PATH, searchParams)
    guardedHandleCancel(targetPath)
  }

  useEffect(() => {
    if (isEditMode && id) {
      loadVideo()
    } else {
      setStatus("Draft")
      setIsFormReady(true);
    }
  }, [id, isEditMode, loadVideo])

  useEffect(() => {
    if (isFormReady) {
      const animationId = requestAnimationFrame(() => {
        commitSnapshot();
      });
      return () => cancelAnimationFrame(animationId);
    }
  }, [isFormReady, commitSnapshot]);

  if (!hasPermission("video", isEditMode ? "update" : "create")) {
    return <AccessDenied />
  }

  if (isEditMode && !isFormReady) {
    return <FormSkeleton mainSections={2} sidebarCards={1} />
  }

  const crumbs = [
    { title: t("Video"), href: createPath(VIDEO.LIST.PATH, searchParams) },
    { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
  ]

  const main = (
    <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={checkChange}>
      <FormSection title={t("Cơ bản")} collapsible={false}>
        <VideoFormFields form={form} isEditMode={isEditMode} />
      </FormSection>
    </Form>
  )

  const sidebar = (
    <FormSection title={t("Xuất bản")} collapsible={false} compact>
      <div className="flex flex-col gap-3">
        <StatusPublish setStatus={setStatus} status={status} />
        <VisibilityPublish status={status} />
      </div>
    </FormSection>
  )

  const actionBar = (
    <StickyActionBar
      right={
        <>
          <Button onClick={handleCancel}>{t("Huỷ")}</Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
            icon={isEditMode ? <CheckCircleOutlined /> : <PlusCircleOutlined />}
          >
            {isEditMode ? t("Cập nhật") : t("Add New Video")}
          </Button>
        </>
      }
    />
  )

  return (
    <FormShell
      title={isEditMode ? t("Edit Video") : t("Add New Video")}
      crumbs={crumbs}
      main={main}
      sidebar={sidebar}
      actionBar={actionBar}
    />
  )
}

export default FormUpsertVideo